import { useEffect, useMemo, useState } from "react";
import EmployeeLayout from "@/components/EmployeeLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ListChecks, Plus, ArrowLeft, CheckCircle2, AlertTriangle, XCircle, Clock, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { ISM_ELEMENTS, ISPS_ITEMS, MLC_AREAS, type FrameworkNode } from "@/pages/auditor/auditorFrameworks";
import AuditChangeLog from "@/components/AuditChangeLog";

type Audit = {
  id: string; audit_number: string | null; title: string; scope: string;
  vessel_name: string | null; lead_auditor: string; audit_team: string | null;
  audit_date: string; closing_date: string | null; status: string;
  summary: string | null; overall_score: number | null; created_at: string;
};

type Item = {
  id: string; audit_id: string; framework: string; clause_ref: string; clause_title: string;
  requirement: string | null; result: string; evidence: string | null;
  finding_notes: string | null; corrective_action: string | null; sort_order: number;
};

const RESULTS = [
  { value: "pending", label: "Pending", color: "bg-muted/40 text-foreground/70", icon: Clock },
  { value: "conform", label: "Conforms", color: "bg-emerald-500/15 text-emerald-300", icon: CheckCircle2 },
  { value: "observation", label: "Observation", color: "bg-amber-500/15 text-amber-300", icon: AlertTriangle },
  { value: "minor_nc", label: "Minor NC", color: "bg-orange-500/15 text-orange-300", icon: AlertTriangle },
  { value: "major_nc", label: "Major NC", color: "bg-red-500/15 text-red-300", icon: XCircle },
  { value: "not_applicable", label: "N/A", color: "bg-muted/30 text-foreground/50", icon: ListChecks },
];

const STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

function defaultItems(auditId: string): Omit<Item, "id">[] {
  const all: FrameworkNode[] = [...ISM_ELEMENTS, ...ISPS_ITEMS, ...MLC_AREAS];
  return all.map((n, i) => ({
    audit_id: auditId,
    framework: n.framework,
    clause_ref: n.ref,
    clause_title: n.label,
    requirement: null,
    result: "pending",
    evidence: null,
    finding_notes: null,
    corrective_action: null,
    sort_order: i,
  }));
}

const InternalAudits = () => {
  const { user, fullName } = useAuth();
  const [audits, setAudits] = useState<Audit[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newScope, setNewScope] = useState("ISM + ISPS + MLC Combined");
  const [newVessel, setNewVessel] = useState("");
  const [newLead, setNewLead] = useState("");
  const [frameworkFilter, setFrameworkFilter] = useState<"ALL" | "ISM" | "ISPS" | "MLC">("ALL");

  const loadAudits = async () => {
    setLoading(true);
    const { data } = await supabase.from("internal_audits" as any).select("*").order("created_at", { ascending: false });
    setAudits((data as any) || []);
    setLoading(false);
  };

  const loadItems = async (id: string) => {
    const { data } = await supabase.from("internal_audit_items" as any).select("*").eq("audit_id", id).order("sort_order");
    setItems((data as any) || []);
  };

  useEffect(() => { loadAudits(); }, []);
  useEffect(() => { if (selectedId) loadItems(selectedId); else setItems([]); }, [selectedId]);

  const selected = audits.find(a => a.id === selectedId) || null;

  const createAudit = async () => {
    if (!newTitle.trim() || !newLead.trim()) {
      toast.error("Title and lead auditor are required");
      return;
    }
    setCreating(true);
    const { data: created, error } = await supabase.from("internal_audits" as any).insert({
      title: newTitle.trim(),
      scope: newScope,
      vessel_name: newVessel.trim() || null,
      lead_auditor: newLead.trim(),
      status: "in_progress",
      created_by: user?.id,
    } as any).select().single();
    if (error || !created) {
      setCreating(false);
      toast.error(error?.message || "Failed to create audit");
      return;
    }
    const auditId = (created as any).id as string;
    const seeds = defaultItems(auditId);
    const { error: itemsErr } = await supabase.from("internal_audit_items" as any).insert(seeds as any);
    if (itemsErr) toast.error("Audit created but items failed: " + itemsErr.message);
    else toast.success("Internal audit created");
    setCreating(false);
    setCreateOpen(false);
    setNewTitle(""); setNewVessel(""); setNewLead("");
    await loadAudits();
    setSelectedId(auditId);
  };

  const updateItem = async (id: string, patch: Partial<Item>) => {
    setItems(prev => prev.map(it => it.id === id ? { ...it, ...patch } : it));
    const { error } = await supabase.from("internal_audit_items" as any).update(patch as any).eq("id", id);
    if (error) toast.error("Save failed: " + error.message);
  };

  const updateAudit = async (patch: Partial<Audit>) => {
    if (!selectedId) return;
    setAudits(prev => prev.map(a => a.id === selectedId ? { ...a, ...patch } : a));
    const { error } = await supabase.from("internal_audits" as any).update(patch as any).eq("id", selectedId);
    if (error) toast.error("Save failed: " + error.message);
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of RESULTS) c[r.value] = 0;
    for (const it of items) c[it.result] = (c[it.result] || 0) + 1;
    return c;
  }, [items]);

  const filteredItems = useMemo(() => {
    if (frameworkFilter === "ALL") return items;
    return items.filter(i => i.framework === frameworkFilter);
  }, [items, frameworkFilter]);

  if (selected) {
    return (
      <EmployeeLayout>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 space-y-6">
          <button onClick={() => setSelectedId(null)} className="flex items-center gap-2 text-sm text-primary-foreground/60 hover:text-primary-foreground">
            <ArrowLeft className="w-4 h-4" /> Back to all audits
          </button>

          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] tracking-[2px] uppercase text-primary-foreground/40 font-bold">{selected.audit_number}</p>
                  <CardTitle className="font-display text-2xl">{selected.title}</CardTitle>
                  <p className="text-sm text-primary-foreground/60 mt-1">{selected.scope}{selected.vessel_name ? ` · ${selected.vessel_name}` : ""}</p>
                </div>
                <Select value={selected.status} onValueChange={(v) => updateAudit({ status: v })}>
                  <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                {RESULTS.map(r => {
                  const Icon = r.icon;
                  return (
                    <div key={r.value} className={`rounded-lg p-3 ${r.color}`}>
                      <div className="flex items-center gap-2 text-xs font-medium">
                        <Icon className="w-3.5 h-3.5" /> {r.label}
                      </div>
                      <p className="text-2xl font-display mt-1">{counts[r.value] || 0}</p>
                    </div>
                  );
                })}
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-primary-foreground/50 font-bold">Executive summary</label>
                <Textarea
                  value={selected.summary || ""}
                  onChange={(e) => setAudits(prev => prev.map(a => a.id === selected.id ? { ...a, summary: e.target.value } : a))}
                  onBlur={(e) => updateAudit({ summary: e.target.value })}
                  rows={3}
                  placeholder="Findings synopsis, scope notes, next steps..."
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3">
              <CardTitle className="font-display text-lg">Checklist</CardTitle>
              <Select value={frameworkFilter} onValueChange={(v: any) => setFrameworkFilter(v)}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All frameworks</SelectItem>
                  <SelectItem value="ISM">ISM Code</SelectItem>
                  <SelectItem value="ISPS">ISPS Code</SelectItem>
                  <SelectItem value="MLC">MLC 2006</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent className="space-y-3">
              {filteredItems.map((it) => {
                const r = RESULTS.find(x => x.value === it.result)!;
                return (
                  <div key={it.id} className="rounded-lg border border-primary-foreground/10 p-4 space-y-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">{it.framework}</Badge>
                          <span className="text-xs font-mono text-primary-foreground/50">{it.clause_ref}</span>
                        </div>
                        <p className="text-sm font-medium mt-1">{it.clause_title}</p>
                      </div>
                      <Select value={it.result} onValueChange={(v) => updateItem(it.id, { result: v })}>
                        <SelectTrigger className={`w-44 ${r.color}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{RESULTS.map(x => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-primary-foreground/40 font-bold">Evidence reviewed</label>
                        <Textarea
                          defaultValue={it.evidence || ""}
                          onBlur={(e) => updateItem(it.id, { evidence: e.target.value })}
                          rows={2}
                          placeholder="Records, procedures, interviews..."
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-primary-foreground/40 font-bold">Finding / observation notes</label>
                        <Textarea
                          defaultValue={it.finding_notes || ""}
                          onBlur={(e) => updateItem(it.id, { finding_notes: e.target.value })}
                          rows={2}
                          placeholder="Describe the NC, observation, or supporting evidence."
                        />
                      </div>
                    </div>
                    {(it.result === "minor_nc" || it.result === "major_nc") && (
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-primary-foreground/40 font-bold">Corrective action</label>
                        <Textarea
                          defaultValue={it.corrective_action || ""}
                          onBlur={(e) => updateItem(it.id, { corrective_action: e.target.value })}
                          rows={2}
                          placeholder="Action, owner, due date."
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredItems.length === 0 && (
                <p className="text-sm text-primary-foreground/50 text-center py-8">No items for this filter.</p>
              )}
            </CardContent>
          </Card>

          <AuditChangeLog auditId={selected.id} />
        </div>
      </EmployeeLayout>
    );
  }

  return (
    <EmployeeLayout>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[10px] tracking-[2px] uppercase font-bold text-lime">
              <FileSignature className="w-3.5 h-3.5" /> QHSE / Compliance
            </div>
            <h1 className="font-display text-3xl mt-1">Internal Audits</h1>
            <p className="text-sm text-primary-foreground/60 mt-1">
              Combined ISM, ISPS &amp; MLC checklists. Live-mirrored to the flag-state auditor portal.
            </p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2"><Plus className="w-4 h-4" /> New audit</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New internal audit</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold">Title</label>
                  <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Q2 2026 SMS Internal Audit" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold">Scope</label>
                  <Input value={newScope} onChange={(e) => setNewScope(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold">Vessel (optional)</label>
                  <Input value={newVessel} onChange={(e) => setNewVessel(e.target.value)} placeholder="e.g. AIRCAT 35" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold">Lead auditor</label>
                  <Input value={newLead || (fullName || "")} onChange={(e) => setNewLead(e.target.value)} placeholder="Name" />
                </div>
                <Button onClick={createAudit} disabled={creating} className="w-full">
                  {creating ? "Creating..." : "Create audit & seed checklist"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <p className="text-sm text-primary-foreground/50">Loading...</p>
        ) : audits.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-sm text-primary-foreground/60">
            No internal audits yet. Click <strong>New audit</strong> to create one.
          </CardContent></Card>
        ) : (
          <div className="grid gap-3">
            {audits.map(a => (
              <button key={a.id} onClick={() => setSelectedId(a.id)} className="text-left">
                <Card className="hover:border-lime/40 transition">
                  <CardContent className="py-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-[10px] tracking-[2px] uppercase text-primary-foreground/40 font-bold">{a.audit_number}</p>
                      <p className="font-display text-lg">{a.title}</p>
                      <p className="text-xs text-primary-foreground/60">
                        {a.scope}{a.vessel_name ? ` · ${a.vessel_name}` : ""} · Lead: {a.lead_auditor} · {new Date(a.audit_date).toLocaleDateString("en-US")}
                      </p>
                    </div>
                    <Badge variant="outline" className="uppercase text-[10px]">{a.status.replace("_", " ")}</Badge>
                  </CardContent>
                </Card>
              </button>
            ))}
          </div>
        )}
      </div>
    </EmployeeLayout>
  );
};

export default InternalAudits;
